import { useState } from "react";
import { motion } from "framer-motion";
import { Ruler, User, Shirt, FileText, Check, Download, Camera, X, Upload } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MeasurementsPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    // Contact Info
    name: "",
    email: "",
    phone: "",
    height: "",
    weight: "",
    // Fit Preference
    fit: "regular",
    stomachType: "normal",
    shoulderType: "normal",
    seatType: "normal",
    // Jacket Body Measurements
    neck: "",
    chest: "",
    stomach: "",
    hip: "",
    shoulderWidth: "",
    frontLength: "",
    backLength: "",
    jacketFirstButtonPosition: "",
    // Jacket Finished Measurements
    finishedChest: "",
    finishedStomach: "",
    finishedShoulder: "",
    bicep: "",
    wrist: "",
    sleeveLengthLeft: "",
    sleeveLengthRight: "",
    // Shirt Finished Measurements
    shirtNeckCollar: "",
    shirtChest: "",
    shirtStomach: "",
    shirtSeat: "",
    shirtBicep: "",
    shirtSleeveLeft: "",
    shirtSleeveRight: "",
    shirtCuffLeft: "",
    shirtCuffRight: "",
    shirtShoulder: "",
    shirtBackLength: "",
    // Pants Body Measurements
    pantsWaist: "",
    pantsHip: "",
    thigh: "",
    knee: "",
    crotch: "",
    leftOuterSeam: "",
    rightOuterSeam: "",
    bottomHemWidth: "",
    // Existing Suit (optional)
    existingJacketSize: "",
    existingPantsSize: "",
    existingBrand: "",
    // Vest Measurements
    vestChest: "",
    vestStomach: "",
    vestBackLength: "",
    vestFrontLength: "",
    vestFirstButtonPosition: "",
    vestBottom: "",
    // Photo uploads
    photos: [],
    // Notes
    notes: ""
  });

  const [photoPreview, setPhotoPreview] = useState([]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + photoPreview.length > 5) {
      toast.error("Maximum 5 photos allowed");
      return;
    }

    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 10MB per photo.`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(prev => [...prev, { name: file.name, url: reader.result }]);
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotoPreview(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/measurements`, formData);
      toast.success("Measurements submitted successfully!", {
        description: "We'll review your measurements and contact you shortly."
      });
      setSubmitted(true);
    } catch (error) {
      toast.error("Failed to submit measurements", {
        description: "Please try again or contact us directly."
      });
    } finally {
      setLoading(false);
    }
  };

  const MeasurementInput = ({ label, instruction, field, required = false }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium">{label} {required && <span className="text-primary">*</span>}</label>
      {instruction && <p className="text-xs text-muted-foreground">{instruction}</p>}
      <div className="flex items-center gap-2">
        <Input
          data-testid={`input-${field}`}
          type="text"
          placeholder="0"
          value={formData[field]}
          onChange={(e) => handleChange(field, e.target.value)}
          className="w-24 bg-card border-white/10 text-center"
          required={required}
        />
        <span className="text-sm text-muted-foreground">inches</span>
      </div>
    </div>
  );

  const RadioOption = ({ name, value, label, currentValue }) => (
    <div className="flex items-center space-x-2">
      <RadioGroupItem 
        value={value} 
        id={`${name}-${value}`}
        className="border-white/30 text-primary"
      />
      <Label htmlFor={`${name}-${value}`} className="text-sm font-light cursor-pointer">{label}</Label>
    </div>
  );

  if (submitted) {
    return (
      <div data-testid="measurements-page" className="min-h-screen pt-20">
        <section className="py-24">
          <div className="max-w-[600px] mx-auto px-6 md:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-20 h-20 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-8">
                <Check size={40} className="text-green-500" />
              </div>
              <h2 className="font-serif text-3xl mb-4">Measurements Received</h2>
              <p className="text-muted-foreground mb-8">
                Thank you for submitting your measurements. Our team will review them and contact you 
                within 24-48 hours to confirm details and discuss next steps.
              </p>
              <Button
                onClick={() => window.location.href = "/booking"}
                className="bg-primary text-primary-foreground px-12 py-6 text-xs uppercase tracking-[0.2em] rounded-none hover:bg-foreground"
              >
                Book a Consultation
              </Button>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="measurements-page" className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="mono-text text-primary mb-6">Self Measure Guide</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6" data-testid="measurements-page-title">
              Submit Your Measurements
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto font-light">
              Use this form to submit your measurements for your custom suit. Keep the measuring tape 
              comfortably snug, but not tight. All measurements should be made to the nearest 1/4 inch.
            </p>
          </motion.div>
        </div>
      </section>

      {/* AI Measurement Option - NEW */}
      <section className="py-8 border-b border-white/5 bg-gradient-to-r from-primary/10 to-primary/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <Camera size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-lg">Try Our AI Measurement Tool</h3>
                <p className="text-sm text-muted-foreground">
                  Don't have a tape? Upload photos and let AI generate your measurements.
                </p>
              </div>
            </div>
            <a
              href="/ai-measurement"
              data-testid="ai-measurement-link"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 text-sm uppercase tracking-wider hover:bg-foreground transition-colors"
            >
              <Camera size={16} strokeWidth={1.5} />
              Get AI Measurements
            </a>
          </div>
        </div>
      </section>

      {/* Download PDF Option */}
      <section className="py-8 border-b border-white/5 bg-card/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Prefer to print and measure offline? Use our printable measurement form.
            </p>
            <a
              href="/measurement-form"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-pdf-btn"
              className="inline-flex items-center gap-2 text-primary hover:text-foreground transition-colors text-sm"
            >
              <Download size={16} strokeWidth={1.5} />
              Print Measurement Form
            </a>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit} className="space-y-12">
            {/* Contact Information */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <User size={24} strokeWidth={1.5} className="text-primary" />
                <h2 className="font-serif text-2xl">Client Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name <span className="text-primary">*</span></label>
                  <Input
                    data-testid="input-name"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="bg-card border-white/10"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email <span className="text-primary">*</span></label>
                  <Input
                    data-testid="input-email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="bg-card border-white/10"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone <span className="text-primary">*</span></label>
                  <Input
                    data-testid="input-phone"
                    type="tel"
                    placeholder="(555) 555-5555"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="bg-card border-white/10"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Height</label>
                  <Input
                    data-testid="input-height"
                    placeholder="e.g., 5'10&quot; or 178cm"
                    value={formData.height}
                    onChange={(e) => handleChange("height", e.target.value)}
                    className="bg-card border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Weight</label>
                  <Input
                    data-testid="input-weight"
                    placeholder="e.g., 170 lbs or 77kg"
                    value={formData.weight}
                    onChange={(e) => handleChange("weight", e.target.value)}
                    className="bg-card border-white/10"
                  />
                </div>
              </div>
            </div>

            {/* Fit Preferences */}
            <div className="space-y-6 p-6 border border-white/10 bg-card/30">
              <h3 className="font-serif text-xl mb-4">Fit Preferences</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium mb-3">Fit Style</label>
                  <RadioGroup value={formData.fit} onValueChange={(v) => handleChange("fit", v)} className="flex flex-wrap gap-4">
                    <RadioOption name="fit" value="slim" label="Slim Fit" currentValue={formData.fit} />
                    <RadioOption name="fit" value="regular" label="Regular" currentValue={formData.fit} />
                    <RadioOption name="fit" value="loose" label="Loose" currentValue={formData.fit} />
                  </RadioGroup>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-3">Stomach</label>
                  <RadioGroup value={formData.stomachType} onValueChange={(v) => handleChange("stomachType", v)} className="flex flex-wrap gap-4">
                    <RadioOption name="stomach" value="thin" label="Thin" currentValue={formData.stomachType} />
                    <RadioOption name="stomach" value="normal" label="Normal" currentValue={formData.stomachType} />
                    <RadioOption name="stomach" value="medium" label="Medium" currentValue={formData.stomachType} />
                    <RadioOption name="stomach" value="large" label="Large" currentValue={formData.stomachType} />
                  </RadioGroup>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-3">Shoulder</label>
                  <RadioGroup value={formData.shoulderType} onValueChange={(v) => handleChange("shoulderType", v)} className="flex flex-wrap gap-4">
                    <RadioOption name="shoulder" value="normal" label="Normal" currentValue={formData.shoulderType} />
                    <RadioOption name="shoulder" value="slope" label="Slope" currentValue={formData.shoulderType} />
                    <RadioOption name="shoulder" value="high" label="High" currentValue={formData.shoulderType} />
                  </RadioGroup>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-3">Seat</label>
                  <RadioGroup value={formData.seatType} onValueChange={(v) => handleChange("seatType", v)} className="flex flex-wrap gap-4">
                    <RadioOption name="seat" value="thin" label="Thin" currentValue={formData.seatType} />
                    <RadioOption name="seat" value="normal" label="Normal" currentValue={formData.seatType} />
                    <RadioOption name="seat" value="large" label="Large" currentValue={formData.seatType} />
                  </RadioGroup>
                </div>
              </div>
            </div>

            {/* Body Measurements */}
            <Accordion type="single" collapsible defaultValue="jacket" className="space-y-4">
              {/* Jacket Measurements */}
              <AccordionItem value="jacket" className="border border-white/10 bg-card/30 px-6">
                <AccordionTrigger className="py-6 hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Shirt size={24} strokeWidth={1.5} className="text-primary" />
                    <span className="font-serif text-xl">Jacket Measurements</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-8">
                  <p className="text-sm text-muted-foreground mb-8">
                    Measure your body directly for the jacket. Keep the tape comfortably snug but not tight.
                  </p>
                  
                  <div className="space-y-8">
                    <h3 className="mono-text text-primary text-xs">Core Measurements</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      <MeasurementInput
                        label="Neck"
                        instruction="Measure around the base of the neck."
                        field="neck"
                        required
                      />
                      <MeasurementInput
                        label="Chest"
                        instruction="Measure around the largest part of your chest, under the arm and level with the nipple."
                        field="chest"
                        required
                      />
                      <MeasurementInput
                        label="Stomach"
                        instruction="Measure around the widest part of your stomach, usually around the belly button."
                        field="stomach"
                        required
                      />
                      <MeasurementInput
                        label="Hip"
                        instruction="Measure around the widest part of your hips."
                        field="hip"
                        required
                      />
                      <MeasurementInput
                        label="Shoulder Width"
                        instruction="Measure from one shoulder point to the other across your back."
                        field="shoulderWidth"
                        required
                      />
                    </div>

                    <div className="gold-line my-8" />

                    <h3 className="mono-text text-primary text-xs">Finished Measurements</h3>
                    <p className="text-xs text-muted-foreground mb-4">Finished garment dimensions</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
                      <MeasurementInput
                        label="Finished Chest"
                        instruction="Desired chest measurement of the finished jacket."
                        field="finishedChest"
                      />
                      <MeasurementInput
                        label="Finished Stomach"
                        instruction="Desired stomach measurement of the finished jacket."
                        field="finishedStomach"
                      />
                      <MeasurementInput
                        label="Finished Shoulder"
                        instruction="Desired shoulder width of the finished jacket."
                        field="finishedShoulder"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                      <MeasurementInput
                        label="Bicep"
                        instruction="Measure around the widest part of your bicep."
                        field="bicep"
                      />
                      <MeasurementInput
                        label="Wrist"
                        instruction="Measure around your wrist."
                        field="wrist"
                      />
                      <MeasurementInput
                        label="Sleeve Length (Left)"
                        instruction="Measure from the shoulder point to the middle cuff at the end of the sleeve."
                        field="sleeveLengthLeft"
                        required
                      />
                      <MeasurementInput
                        label="Sleeve Length (Right)"
                        instruction="Measure from the shoulder point to the middle cuff at the end of the sleeve."
                        field="sleeveLengthRight"
                        required
                      />
                    </div>

                    <div className="gold-line my-8" />

                    <h3 className="mono-text text-primary text-xs">Length</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      <MeasurementInput
                        label="Front Length"
                        instruction="Measure from shoulder point down the front to desired jacket length."
                        field="frontLength"
                        required
                      />
                      <MeasurementInput
                        label="Back Length"
                        instruction="Measure from bottom of collar to desired jacket length along center back."
                        field="backLength"
                        required
                      />
                      <MeasurementInput
                        label="1st Button Position"
                        instruction="Distance from top of jacket lapel to the first button."
                        field="jacketFirstButtonPosition"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Shirt Finished Measurements */}
              <AccordionItem value="shirt" className="border border-white/10 bg-card/30 px-6">
                <AccordionTrigger className="py-6 hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Ruler size={24} strokeWidth={1.5} className="text-primary" />
                    <span className="font-serif text-xl">Shirt Finished Measurements</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-8">
                  <p className="text-sm text-muted-foreground mb-8">
                    Enter the finished garment measurements for custom shirts.
                  </p>
                  
                  <div className="space-y-8">
                    <h3 className="mono-text text-primary text-xs">Body Measurements</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                      <MeasurementInput
                        label="Neck (Shirt Collar)"
                        instruction="Measure around your neck where the collar would sit."
                        field="shirtNeckCollar"
                      />
                      <MeasurementInput
                        label="Chest"
                        instruction="Finished chest measurement of the shirt."
                        field="shirtChest"
                      />
                      <MeasurementInput
                        label="Stomach"
                        instruction="Finished stomach measurement of the shirt."
                        field="shirtStomach"
                      />
                      <MeasurementInput
                        label="Seat"
                        instruction="Finished seat/hip measurement of the shirt."
                        field="shirtSeat"
                      />
                    </div>

                    <div className="gold-line my-8" />

                    <h3 className="mono-text text-primary text-xs">Shoulder & Length</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      <MeasurementInput
                        label="Shoulder"
                        instruction="Measure shoulder seam to shoulder seam across the back."
                        field="shirtShoulder"
                      />
                      <MeasurementInput
                        label="Shirt Back Length"
                        instruction="Measure from the base of collar to the bottom of the shirt."
                        field="shirtBackLength"
                      />
                      <MeasurementInput
                        label="Bicep"
                        instruction="Measure around the widest part of the upper arm."
                        field="shirtBicep"
                      />
                    </div>

                    <div className="gold-line my-8" />

                    <h3 className="mono-text text-primary text-xs">Sleeves & Cuffs</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                      <MeasurementInput
                        label="Sleeve (Left)"
                        instruction="Measure from shoulder seam to wrist."
                        field="shirtSleeveLeft"
                      />
                      <MeasurementInput
                        label="Sleeve (Right)"
                        instruction="Measure from shoulder seam to wrist."
                        field="shirtSleeveRight"
                      />
                      <MeasurementInput
                        label="Cuff (Left)"
                        instruction="Measure around the left wrist for cuff size."
                        field="shirtCuffLeft"
                      />
                      <MeasurementInput
                        label="Cuff (Right)"
                        instruction="Measure around the right wrist for cuff size."
                        field="shirtCuffRight"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Pants Measurements */}
              <AccordionItem value="pants" className="border border-white/10 bg-card/30 px-6">
                <AccordionTrigger className="py-6 hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Ruler size={24} strokeWidth={1.5} className="text-primary" />
                    <span className="font-serif text-xl">Pants Measurements</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-8">
                  <p className="text-sm text-muted-foreground mb-8">
                    Measure your lower body for the pants. Keep the tape comfortably snug but not tight.
                  </p>
                  
                  <div className="space-y-8">
                    <h3 className="mono-text text-primary text-xs">Core Measurements</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      <MeasurementInput
                        label="Waist"
                        instruction="Measure where you want your pants to sit, all the way around."
                        field="pantsWaist"
                        required
                      />
                      <MeasurementInput
                        label="Hip"
                        instruction="Measure around the widest part of your hips."
                        field="pantsHip"
                        required
                      />
                      <MeasurementInput
                        label="Thigh"
                        instruction="Measure around the widest part of your thigh, just below the crotch."
                        field="thigh"
                        required
                      />
                      <MeasurementInput
                        label="Knee"
                        instruction="Measure around your knee with leg slightly bent."
                        field="knee"
                      />
                      <MeasurementInput
                        label="Crotch"
                        instruction="Measure from waistband front, through legs, to waistband back."
                        field="crotch"
                        required
                      />
                      <MeasurementInput
                        label="Bottom Hem Width"
                        instruction="Desired width at the bottom of the pant leg."
                        field="bottomHemWidth"
                      />
                    </div>

                    <div className="gold-line my-8" />

                    <h3 className="mono-text text-primary text-xs">Length</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <MeasurementInput
                        label="Left Outer Seam"
                        instruction="Measure from waistband to desired length on outside of left leg."
                        field="leftOuterSeam"
                        required
                      />
                      <MeasurementInput
                        label="Right Outer Seam"
                        instruction="Measure from waistband to desired length on outside of right leg."
                        field="rightOuterSeam"
                        required
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Existing Suit Reference */}
              <AccordionItem value="existing" className="border border-white/10 bg-card/30 px-6">
                <AccordionTrigger className="py-6 hover:no-underline">
                  <div className="flex items-center gap-3">
                    <FileText size={24} strokeWidth={1.5} className="text-primary" />
                    <span className="font-serif text-xl">Existing Suit Reference (Optional)</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-8">
                  <p className="text-sm text-muted-foreground mb-8">
                    If you have an existing suit that fits you well, provide the size and brand as reference.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">Jacket Size</label>
                      <Input
                        data-testid="input-existingJacketSize"
                        placeholder="e.g., 40 R"
                        value={formData.existingJacketSize}
                        onChange={(e) => handleChange("existingJacketSize", e.target.value)}
                        className="bg-card border-white/10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Pants Size</label>
                      <Input
                        data-testid="input-existingPantsSize"
                        placeholder="e.g., 34 R or 34/32"
                        value={formData.existingPantsSize}
                        onChange={(e) => handleChange("existingPantsSize", e.target.value)}
                        className="bg-card border-white/10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Brand Name</label>
                      <Input
                        data-testid="input-existingBrand"
                        placeholder="Brand name"
                        value={formData.existingBrand}
                        onChange={(e) => handleChange("existingBrand", e.target.value)}
                        className="bg-card border-white/10"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Vest Measurements */}
              <AccordionItem value="vest" className="border border-white/10 bg-card/30 px-6">
                <AccordionTrigger className="py-6 hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Shirt size={24} strokeWidth={1.5} className="text-primary" />
                    <span className="font-serif text-xl">Vest Measurements (Optional)</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-8">
                  <p className="text-sm text-muted-foreground mb-8">
                    If you'd like a vest included with your suit, please provide these measurements.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <MeasurementInput
                      label="Vest Chest"
                      instruction="Measure around the chest where the vest will sit. (Range: 27.95-73.62)"
                      field="vestChest"
                    />
                    <MeasurementInput
                      label="Vest Stomach"
                      instruction="Measure around the stomach at vest level. (Range: 25.98-80.31)"
                      field="vestStomach"
                    />
                    <MeasurementInput
                      label="Vest Back Length"
                      instruction="Measure from base of neck to desired vest bottom along back. (Range: 15.75-30.71)"
                      field="vestBackLength"
                    />
                    <MeasurementInput
                      label="Vest Front Length"
                      instruction="Measure from shoulder to desired vest bottom along front. (Range: 14.17-37.40)"
                      field="vestFrontLength"
                    />
                    <MeasurementInput
                      label="1st Button Position"
                      instruction="Distance from top of vest to first button. (Range: 5.91-25.59)"
                      field="vestFirstButtonPosition"
                    />
                    <MeasurementInput
                      label="Vest Bottom"
                      instruction="Measure around at the bottom hem of the vest. (Range: 23.62-78.74)"
                      field="vestBottom"
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Photo Upload */}
              <AccordionItem value="photos" className="border border-white/10 bg-card/30 px-6">
                <AccordionTrigger className="py-6 hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Camera size={24} strokeWidth={1.5} className="text-primary" />
                    <span className="font-serif text-xl">Body Photos (Recommended)</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-8">
                  <p className="text-sm text-muted-foreground mb-6">
                    Upload photos of yourself in a well-fitting suit or dress shirt. This helps us understand your body type 
                    and current fit preferences. We recommend including front, side, and back views.
                  </p>

                  <div className="space-y-6">
                    {/* Upload Area */}
                    <div className="border-2 border-dashed border-white/20 hover:border-primary/50 transition-colors p-8 text-center">
                      <input
                        type="file"
                        id="photo-upload"
                        data-testid="photo-upload-input"
                        multiple
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                      <label htmlFor="photo-upload" className="cursor-pointer">
                        <Upload size={40} strokeWidth={1} className="text-primary mx-auto mb-4" />
                        <p className="text-foreground mb-2">Click to upload photos</p>
                        <p className="text-xs text-muted-foreground">PNG, JPG up to 10MB each (Max 5 photos)</p>
                      </label>
                    </div>

                    {/* Photo Previews */}
                    {photoPreview.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {photoPreview.map((photo, index) => (
                          <div key={photo.url || `photo-${index}`} className="relative group aspect-square">
                            <img
                              src={photo.url}
                              alt={`Upload ${index + 1}`}
                              className="w-full h-full object-cover border border-white/10"
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(index)}
                              className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`remove-photo-${index}`}
                            >
                              <X size={14} />
                            </button>
                            <p className="text-xs text-muted-foreground mt-1 truncate">{photo.name}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="bg-card/50 p-4 border border-white/5">
                      <p className="mono-text text-primary text-[10px] mb-2">Photo Tips</p>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• Wear a well-fitting suit or dress shirt</li>
                        <li>• Stand naturally with arms at your sides</li>
                        <li>• Include front, side (profile), and back views</li>
                        <li>• Good lighting helps us see fit details</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Additional Notes */}
            <div className="space-y-4">
              <label className="block text-sm font-medium">Additional Notes / Remarks</label>
              <Textarea
                data-testid="input-notes"
                placeholder="Any specific preferences, fit concerns, style details, or special requests..."
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                className="bg-card border-white/10 min-h-[120px] resize-none"
              />
            </div>

            {/* Submit */}
            <div className="flex justify-center pt-8">
              <Button
                type="submit"
                data-testid="submit-measurements-btn"
                disabled={loading}
                className="bg-primary text-primary-foreground px-16 py-6 text-xs uppercase tracking-[0.2em] rounded-none hover:bg-foreground transition-all duration-300"
              >
                {loading ? "Submitting..." : "Submit Measurements"}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default MeasurementsPage;
